import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { combineLatest, debounceTime, ObservedValueOf, Subscription, of } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { MessageService, MenuItem } from 'primeng/api';

import { JournodexSubjectsService } from '../../../../Common/Journodex/services/journodex-subjects.service';
import { DiscoverRoutingService } from '../../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteModel } from '../../../DiscoverRouter/models/discover-route.model';
import { DiscoverRouteTypeEnum } from '../../../DiscoverRouter/enums/discover-route-type.enum';
import { TargetingSearch } from '../../models/targeting-search.model';
import { TargetingSearchesService } from '../../services/targeting-searches.service';
import { JournodexEnumsService } from '../../../Core/services/journodex-enums.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TabsStyleTypesEnum } from '../../../../Common/Layout/enums/tabs-types.enum';
import {
    SearchModeEnum,
    searchModeEnumToTabIndexMap,
    tabIndexToSearchModeEnumMap
} from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizedEnum } from '../../../../api/journodex/model/localized-enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import type { ContactSearchComponent } from './ContactSearch/contact-search.component';
import type { OutletSearchComponent } from './OutletSearch/outlet-search.component';
import { TargetingSearchFullModel } from '../../models/targeting-search-full.model';
import { TargetingGeographyService } from '../../../../Common/TargetingShared/services/targeting-geography-service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { TargetingPreferencesService } from '@Common/TargetingShared/services/targeting-preferences.service';
import { TargetingAccountPreferencesApiModel } from '@api/targeting/model/targeting-account-preferences.api.model';
import { TargetingSearchFormService } from '@Common/TargetingShared/services/targeting-search-form.service';
import { DistributionPickerService } from '@Common/RhpContexts/services/distribution-picker.service';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { TabsComponent } from '@Common/Layout/components/Tabs/tabs.component';
import { JournodexSubjectParentModel } from '@Common/Journodex/models/journodex.subject.model';
import { JobRoleModel } from '@Discover/Core/models/job-role.model';
import { ContactModel } from '@Common/TargetingShared/model/contact.model';
import { TargetingMediaTypeEnum } from '@Discover/Targeting/enums/targeting-media-type.enum';
import { TargetingSearchPendoEventEnum } from '@Discover/Targeting/enums/targeting-search-pendo-event.enum';

class SearchErrors {
    hasNoSearchName: boolean = false;
    searchNameDuplicate: boolean = false;
}
interface TargetingSearchForm {
    general: FormGroup;
    contactForm?: ObservedValueOf<ContactSearchComponent['formReady']>;
    outletForm?: ObservedValueOf<OutletSearchComponent['formReady']>;
};

@Component({
    selector: 'agpr-targeting-search',
    templateUrl: './targeting-search.component.html',
    styleUrls: ['./targeting-search.component.scss'],
    providers: [
        {
            provide: 'TargetSearchDistributionPickerService',
            useClass: DistributionPickerService
        },
        {
            provide: DistributionPickerService,
            useExisting: 'TargetSearchDistributionPickerService'
        }
    ],
    standalone: false
})
export class TargetingSearchComponent implements OnInit, OnDestroy {

    @ViewChild(TabsComponent, {static: false}) tabs: TabsComponent;

    targetingSearch: TargetingSearch;
    targetingSearchFull: TargetingSearchFullModel;

    searchDirty = false;
    errors: SearchErrors = new SearchErrors();

    selectedTabIndex: number = 0;
    productTypes: LocalizedEnum[] = [];
    compatibleProductTypes: string[] = [];
    incompatibleProductTypes: string[] = [];

    loading = false;
    showError: boolean = false;
    noSearchParameter = false;

    previewCountString = '';
    previewCount: number = Infinity;
    previewLoading = false;
    hasUnavailableOutlet: boolean = false;

    searchForm: FormGroup = new FormGroup({
        general: new FormGroup({
            name: new FormControl(''),
            mediaTypes: new FormControl(new IncludeExcludeModel<TargetingMediaTypeEnum>()),
            productTypes: new FormControl(new IncludeExcludeModel<string>()),
            marketSectors: new FormControl(new IncludeExcludeModel<string>()),
            coverageFocuses: new FormControl(new IncludeExcludeModel<string>()),
            productFrequencies: new FormControl(new IncludeExcludeModel<string>()),
            targetAudiences: new FormControl(new IncludeExcludeModel<string>()),
            audienceReachType: new FormControl(null),
            audienceReachMin: new FormControl({ value: null, disabled: true }),
            audienceReachMax: new FormControl({ value: null, disabled: true }),
            includePrivateContacts: new FormControl(true),
            groupByOutlet: new FormControl(false)
        })
    });

    circulationTypes: LocalizedEnum[];
    circulationTypeOptions: DropdownOptionModel[];

    private targetingAccountPreferences: TargetingAccountPreferencesApiModel = null;

    // @TODO: Remove this, this is a hack to allow greater testing on prod
    public showAiHelper: boolean = false;

    private currentRoute: DiscoverRouteModel;
    private subscriptions: Subscription[] = [];

    readonly reachMinimum = 0;
    readonly reachMaximum = 999999999999;
    readonly MAX_SEARCH_SIZE = TargetingSearch.MAX_SEARCH_SIZE;

    readonly buttonTypes = ButtonTypesEnum;
    readonly searchModes = SearchModeEnum;
    readonly tabsStyleTypesEnum = TabsStyleTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly saveActions: MenuItem[] = [
        {
            label: this.localizationService.transform('Discover.targeting.actions.saveChanges'),
            command: () => this.saveSearch()
        },
        {
            label: this.localizationService.transform('Discover.targeting.actions.saveAsNewSearch'),
            command: () => this.openCreateDialog()
        }
    ];

    constructor(
        public journodexSubjectsService: JournodexSubjectsService,
        protected messageService: MessageService,
        public targetingSearchesService: TargetingSearchesService,
        protected discoverRoutingService: DiscoverRoutingService,
        public journodexEnumsService: JournodexEnumsService,
        public targetingGeographyService: TargetingGeographyService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        protected targetingPreferencesService: TargetingPreferencesService,
        protected targetingSearchFormService: TargetingSearchFormService,
        @Inject('TargetSearchDistributionPickerService') public distributionPickerService: DistributionPickerService,
    ) { }

    ngOnInit() {
        this.distributionPickerService.useLocking = true;
        this.distributionPickerService.loadWorkingDistributionsFromSession();
        this.subscriptions.push(
            this.searchForm.get('general.audienceReachType').valueChanges.subscribe(type => {
                if (type) {
                    this.searchForm.get('general.audienceReachMin').enable();
                    this.searchForm.get('general.audienceReachMax').enable();
                } else {
                    this.searchForm.get('general.audienceReachMin').disable();
                    this.searchForm.get('general.audienceReachMax').disable();
                }
            }),
            combineLatest([
                this.journodexEnumsService.getCirculationTypes(),
                this.targetingSearchesService.currentResource$.pipe(
                    filter(x => !!x),
                    tap(search => {
                        this.targetingSearch = search;
                        this.targetingSearchesService.fetchPreviewCount(search);
                    }),
                    switchMap(fullSearch => {
                        return this.targetingSearchFormService.constructFullSearch(fullSearch);
                    })
                )
            ]).subscribe(([circulationTypes, search]) => {
                if (circulationTypes) {
                    this.circulationTypes = circulationTypes;
                    this.circulationTypeOptions = circulationTypes?.map(x => {
                        return new DropdownOptionModel(x.displayString, x.enumValue);
                    });
                }

                this.targetingSearchFull = new TargetingSearchFullModel(search);

                this.searchForm.patchValue({
                    general: {
                        name: search.name ?? null,
                        mediaTypes: search.mediaTypes,
                        productTypes: search.productTypes,
                        marketSectors: search.marketSectors,
                        coverageFocuses: search.coverageFocuses,
                        productFrequencies: search.productFrequencies,
                        targetAudiences: search.targetAudiences,
                        audienceReachType: this.circulationTypeOptions?.find(x => x.value === search?.audienceReachType) ?? null,
                        audienceReachMin: search.audienceReachMin ?? null,
                        audienceReachMax: search.audienceReachMax ?? null,
                        includePrivateContacts: search.includePrivateContacts,
                        groupByOutlet: search.groupByOutlet
                    }
                });

                const searchMode = (this.targetingSearch?.searchMode) ?
                    this.targetingSearch.searchMode : SearchModeEnum.contact;
                this.selectedTabIndex = tabIndexToSearchModeEnumMap.get(searchMode);
            }),
            this.searchForm.get('general').valueChanges.pipe(
                // @hack - fixes timing issues between valueChanges, patchValue
                debounceTime(300),
            ).subscribe(formValues => {
                const targetingSearch: Partial<TargetingSearchFullModel> = {
                    name: formValues.name,
                    mediaTypes: formValues.mediaTypes,
                    productTypes: formValues.productTypes,
                    marketSectors: formValues.marketSectors,
                    coverageFocuses: formValues.coverageFocuses,
                    productFrequencies: formValues.productFrequencies,
                    targetAudiences: formValues.targetAudiences,
                    audienceReachType: formValues.audienceReachType?.value,
                    audienceReachMin: formValues.audienceReachMin ?? null,
                    audienceReachMax: formValues.audienceReachMax ?? null,
                    includePrivateContacts: formValues.includePrivateContacts,
                    groupByOutlet: formValues.groupByOutlet
                };
                this.updateSearch(targetingSearch);
            }),
            this.searchForm.get('general.mediaTypes').valueChanges.subscribe((x: IncludeExcludeModel<TargetingMediaTypeEnum>) => {
                this.journodexEnumsService.updateCompatibleProductTypes([...x.include, ...x.includeAll]);
            }),
            this.searchForm.get('general.productTypes').valueChanges.subscribe(_ => {
                this.compareProductTypes();
            }),
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    this.currentRoute = route;
                }
            ),
            this.targetingSearchesService.resourceDirty$.subscribe(x => {
                this.searchDirty = x;
                this.showError = false;
            }),
            this.targetingSearchesService.getPreviewCount().subscribe(count => {
                if (!count && count !== 0) {
                    this.previewCountString = ' (>10000)';
                    this.previewCount = Infinity;
                    return;
                }
                this.previewCount = count;
                this.previewCountString = (count > 10000) ? ' (>10000)' : ' (' + count + ')' ;
            }),
            this.targetingSearchesService.getPreviewCountLoading().subscribe(loading => {
                if (loading) {
                    this.previewCountString = '';
                }
                this.previewLoading = loading;
            }),
            this.targetingSearchesService.getLoading().subscribe(x => this.loading = x),
            this.journodexEnumsService.getProductTypes().subscribe(x => this.productTypes = x),
            this.journodexEnumsService.getCompatibleProductTypes().subscribe(types => {
                if (!types) {
                    return;
                }
                this.compatibleProductTypes = types;
                this.compareProductTypes();
            }),
            this.currentUserStateService.accountHaveAiPermissions().subscribe(
                showAiHelper => {
                    this.showAiHelper = showAiHelper;
                }
            ),
            this.targetingPreferencesService.targetingPreferences$.pipe(filter(x => !!x)).subscribe(preferences => {
                this.targetingAccountPreferences = preferences;
            }),
            this.targetingSearchesService.unavailableOutlet$.subscribe(value => this.hasUnavailableOutlet = value),
        );
    }

    get audienceReachMin() {
        return this.searchForm.get('general.audienceReachMin');
    }

    get audienceReachMax() {
        return this.searchForm.get('general.audienceReachMax');
    }

    /**
     * Merges new changes into targetingSearch & update dirty flag
     */
    updateSearch(search: Partial<TargetingSearchFullModel>) {
        this.targetingSearchFull.merge(search);
        this.targetingSearch.merge(this.targetingSearchFull.toBaseSearch());

        this.targetingSearchesService.currentResourceModified();
        this.targetingSearchesService.fetchPreviewCount(this.targetingSearch);
    }

    recommendedRecipientSearch() {
        this.targetingSearch.isRecommendedRecipientSearch = true;

        if (this.targetingAccountPreferences) {
            this.targetingSearch.geographies = new IncludeExcludeModel(this.targetingAccountPreferences.geography);
        }

        this.navigateToSearchResults();
    }

    /** Navigate to proper results page route, where a subsequent search will be performed */
    navigateToSearchResults() {
        this.targetingSearchesService.trackSearchEvent(TargetingSearchPendoEventEnum.RUN, this.targetingSearch);

        if (this.currentRoute.type === DiscoverRouteTypeEnum.targetingSearch ||
            this.currentRoute.type === DiscoverRouteTypeEnum.targetingSearchUpdate) {
            this.discoverRoutingService.gotoTargetingSearchResults();
        } else if (this.currentRoute.type === DiscoverRouteTypeEnum.targetingSavedSearch) {
            this.discoverRoutingService.gotoTargetingSavedSearchResults(this.targetingSearch.id);
        }
    }

    saveSearch() {
        this.targetingSearchesService.trackSearchEvent(TargetingSearchPendoEventEnum.SAVE, this.targetingSearch);

        this.targetingSearchesService.getValidationErrors(this.targetingSearch, false).pipe(
            switchMap((errors) => {
                this.errors = errors;
                if (!this.errors.hasNoSearchName && !this.errors.searchNameDuplicate) {
                    return this.targetingSearchesService.update(this.targetingSearch);
                }
                return of(null);
            })
        ).subscribe();
    }

    openCreateDialog() {
        this.targetingSearchesService.openCreateDialog(this.targetingSearch);
    }

    restoreCachedSearch() {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'TargetingSearch',
                heading: 'Common.searches.confirmResetHeader',
                messageHtml: 'Common.searches.confirmResetMessage',
                okLabel: 'Discover.targeting.actions.reset'
            },
            outputs: {
                dialogClose: async (confirmed: boolean) => {
                    if (confirmed) {
                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: 'Discover.dialogs.resetSearch.heading',
                            detail: 'Discover.dialogs.resetSearch.message'
                        });

                        this.targetingSearchesService.resetToCachedResource();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }


    addChildForm<K extends keyof TargetingSearchForm>(name: K, group: Exclude<TargetingSearchForm[K], undefined>) {
        this.searchForm.setControl(name, group);
    }

    tabChange(tabIndex: number) {
        const newMode = searchModeEnumToTabIndexMap.get(tabIndex);

        if (this.targetingSearch.searchMode === newMode) {

            return;
        }

        if (this.canConvertToModeSeamlessly(newMode) === false) {
            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'MonitoringSearchs',
                    heading: 'Discover.dialogs.modeSwitch.header',
                    messageHtml: 'Discover.dialogs.modeSwitch.message',
                    okLabel: 'Monitoring.confirm'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.selectedTabIndex = tabIndex;
                            this.switchMode(newMode);
                        } else {
                            requestAnimationFrame(() => {
                                this.tabs?.selectTabByIndex(tabIndex === 0 ? 1: 0);
                            });
                        }
                    }
                }
            };

            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            this.selectedTabIndex = tabIndex;
            this.switchMode(newMode);
        }
    }

    compareProductTypes() {
        this.incompatibleProductTypes = [];
        this.searchForm.get('general.productTypes').value?.include.forEach(productType => {
            if (!this.compatibleProductTypes.includes(productType)) {
                this.incompatibleProductTypes.push(this.productTypes.find(x => x.enumValue === productType)?.displayString);
            }
        });
    }

    private switchMode(newMode: SearchModeEnum) {
        if (newMode === SearchModeEnum.outlet) {
            const newValues: Partial<TargetingSearchFullModel> = {
                keywords: new IncludeExcludeModel<string>(),
                subjects: new IncludeExcludeModel<JournodexSubjectParentModel>(),
                jobRoles: new IncludeExcludeModel<JobRoleModel>(),
                jobTitles: new IncludeExcludeModel<string>(),
                contacts: <ContactModel[]>[]
            };

            this.searchForm.patchValue({
                contactForm: newValues
            });
            this.updateSearch(newValues);
        }
        this.targetingSearch.searchMode = newMode;
        this.targetingSearchesService.currentResourceModified();
    }

    /**
     * Check for data loss when switching from contact to outlet
     * @param newMode the desired mode to be switched to
     * @returns true if no data loss will occur, otherwise false
     */
    private canConvertToModeSeamlessly(newMode: SearchModeEnum): boolean {
        if (this.targetingSearch.searchMode === SearchModeEnum.contact && newMode === SearchModeEnum.outlet) {
            return (
                this.targetingSearch.jobRoles.isEmpty() &&
                this.targetingSearch.contactIdsNames.include.length === 0 &&
                this.targetingSearch.jobTitles.isEmpty() &&
                this.targetingSearch.subjects.isEmpty() &&
                this.targetingSearch.monitoringKeywords.isEmpty()
            );
        } else {
            return true;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
